import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap, timeout } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { PlatformRole, PlatformUser } from '../models/platform-access.models';
import { isUuidV7 } from '../utils/public-id.utils';
import { GoogleIdentityService } from './google-identity.service';

export type User = PlatformUser;

const REFRESH_RETRY_INITIAL_DELAY_MS = 30_000;
const REFRESH_RETRY_MAX_DELAY_MS = 5 * 60_000;
const USER_IDLE_REFRESH_THRESHOLD_MS = 60_000;

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name?: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  user?: User;
  tokens?: AuthTokens;
  requiresMfa?: boolean;
  mfaChallenge?: MfaChallenge;
  returnUrl?: string;
  message?: string;
  error?: {
    code: number;
    message: string;
    details?: string[];
  };
}

export interface MfaMethod {
  publicId: string;
  type: 'totp' | 'email';
  label: string;
}

export interface MfaChallenge {
  id: string;
  methods: MfaMethod[];
  expiresAt: string;
}

export interface AuthProviders {
  google: boolean;
  googleClientId: string | null;
}

export interface PasswordRequirements {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly API_URL = environment.apiUrl;
  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_KEY = 'current_user';

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private isLoadingSubject = new BehaviorSubject<boolean>(true);

  public currentUser$ = this.currentUserSubject.asObservable();
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  public isLoading$ = this.isLoadingSubject.asObservable();

  private refreshTokenTimeout: ReturnType<typeof setTimeout> | null = null;
  private refreshInFlight$: Observable<AuthResponse> | null = null;
  private refreshWhenVisible = false;
  private refreshWhenActive = false;
  private refreshRetryDelayMs = REFRESH_RETRY_INITIAL_DELAY_MS;
  private lastUserActivityAt = Date.now();
  private sessionExpiryInProgress = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private googleIdentityService: GoogleIdentityService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.document.addEventListener('visibilitychange', () => this.refreshWhenDocumentVisible());
    this.document.addEventListener('pointerdown', () => this.recordUserActivity(), true);
    this.document.addEventListener('keydown', () => this.recordUserActivity(), true);
    this.initializeAuth();
  }

  private initializeAuth(): void {
    const accessToken = this.getAccessToken();
    const user = this.getStoredUser();

    if (accessToken) {
      if (user) {
        this.currentUserSubject.next(user);
      }
      this.isAuthenticatedSubject.next(true);
      this.startRefreshTokenTimer();
    }

    this.isLoadingSubject.next(false);
  }

  login(credentials: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}/auth/login`, credentials).pipe(
      tap(response => {
        if (response.success && response.user && response.tokens) {
          this.handleAuthSuccess(response.user, response.tokens);
        }
      }),
      catchError(this.handleError)
    );
  }

  register(data: RegisterRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}/auth/register`, data).pipe(
      tap(response => {
        if (response.success && response.user && response.tokens) {
          this.handleAuthSuccess(response.user, response.tokens);
        }
      }),
      catchError(this.handleError)
    );
  }

  forgotPassword(data: ForgotPasswordRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}/auth/forgot-password`, data).pipe(
      catchError(this.handleError)
    );
  }

  resetPassword(data: ResetPasswordRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}/auth/reset-password`, data).pipe(
      catchError(this.handleError)
    );
  }

  getProviders(): Observable<AuthProviders> {
    return this.http.get<{ success: boolean; providers: AuthProviders }>(`${this.API_URL}/auth/providers`).pipe(
      map((response) => response.providers),
      catchError(this.handleError)
    );
  }

  startGoogleSignIn(returnUrl: string): void {
    const url = new URL(`${this.API_URL}/auth/google/start`);
    url.searchParams.set('returnUrl', this.getSafeReturnUrl(returnUrl) || '/');
    window.location.assign(url.toString());
  }

  completeGoogleSignIn(handoff: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}/auth/google/complete`, { handoff }).pipe(
      tap((response) => {
        if (response.success && response.user && response.tokens) {
          this.handleAuthSuccess(response.user, response.tokens);
        }
      }),
      catchError(this.handleError)
    );
  }

  signInWithGoogleCredential(credential: string, returnUrl: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}/auth/google/credential`, {
      credential,
      returnUrl: this.getSafeReturnUrl(returnUrl) || '/',
    }).pipe(
      tap((response) => {
        if (response.success && response.user && response.tokens) {
          this.handleAuthSuccess(response.user, response.tokens);
        }
      }),
      catchError(this.handleError)
    );
  }

  getMfaChallenge(challengeId: string): Observable<MfaChallenge> {
    return this.http.get<{ success: boolean; methods: MfaMethod[]; expiresAt: string }>(
      `${this.API_URL}/auth/mfa/challenge`,
      { params: { challengeId } }
    ).pipe(
      map((response) => ({ id: challengeId, methods: response.methods, expiresAt: response.expiresAt })),
      catchError(this.handleError)
    );
  }

  sendMfaEmailCode(challengeId: string): Observable<{ message: string; expiresAt: string }> {
    return this.http.post<{ success: boolean; message: string; expiresAt: string }>(
      `${this.API_URL}/auth/mfa/email/send`,
      { challengeId }
    ).pipe(
      map((response) => ({ message: response.message, expiresAt: response.expiresAt })),
      catchError(this.handleError)
    );
  }

  verifyMfa(challengeId: string, method: 'totp' | 'email' | 'recovery', code: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}/auth/mfa/verify`, { challengeId, method, code }).pipe(
      tap((response) => {
        if (response.success && response.user && response.tokens) {
          this.handleAuthSuccess(response.user, response.tokens);
        }
      }),
      catchError(this.handleError)
    );
  }

  logout(): Observable<AuthResponse> {
    const refreshToken = this.getRefreshToken();
    this.googleIdentityService.disableAutoSelect();
    sessionStorage.setItem('google_one_tap_suppressed', 'true');
    this.clearAuth();

    if (!refreshToken) {
      return of({ success: true, message: 'Logged out' });
    }

    this.http.post<AuthResponse>(`${this.API_URL}/auth/logout`, { refreshToken }).pipe(
      catchError(() => of({ success: true, message: 'Logged out' }))
    ).subscribe();

    return of({ success: true, message: 'Logged out' });
  }

  refreshToken(): Observable<AuthResponse> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      this.handleSessionExpiry();
      return throwError(() => new Error('No refresh token available'));
    }

    const request$ = this.http.post<AuthResponse>(`${this.API_URL}/auth/refresh`, { refreshToken }).pipe(
      timeout(5_000),
      tap(response => {
        if (response.success && response.tokens) {
          this.storeTokens(response.tokens);
          if (response.user) {
            this.storeUser(response.user);
            this.currentUserSubject.next(response.user);
          }
          this.isAuthenticatedSubject.next(true);
          this.refreshRetryDelayMs = REFRESH_RETRY_INITIAL_DELAY_MS;
          this.startRefreshTokenTimer();
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 || error.status === 403) {
          this.handleSessionExpiry();
        } else {
          this.scheduleRefreshRetry();
        }
        return throwError(() => error);
      }),
      finalize(() => {
        this.refreshInFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.refreshInFlight$ = request$;
    return request$;
  }

  getCurrentUser(): Observable<AuthResponse> {
    return this.http.get<AuthResponse>(`${this.API_URL}/auth/me`).pipe(
      tap(response => {
        if (response.success && response.user) {
          this.currentUserSubject.next(response.user);
          this.storeUser(response.user);
        }
      }),
      catchError(this.handleError)
    );
  }

  private handleAuthSuccess(user: User, tokens: AuthTokens): void {
    this.storeTokens(tokens);
    this.storeUser(user);
    this.currentUserSubject.next(user);
    this.isAuthenticatedSubject.next(true);
    this.startRefreshTokenTimer();
  }

  private clearAuth(): void {
    this.stopRefreshTokenTimer();
    this.refreshWhenVisible = false;
    this.refreshWhenActive = false;
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);
  }

  private storeTokens(tokens: AuthTokens): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refreshToken);
  }

  private storeUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  getCurrentUserSnapshot(): User | null {
    return this.currentUserSubject.value;
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  private getStoredUser(): User | null {
    const userJson = localStorage.getItem(this.USER_KEY);
    if (userJson) {
      try {
        const parsed = JSON.parse(userJson) as Partial<User>;
        if (
          typeof parsed.publicId === 'string' &&
          isUuidV7(parsed.publicId) &&
          typeof parsed.email === 'string' &&
          typeof parsed.platformRole === 'string' &&
          parsed.plan
        ) {
          return parsed as User;
        }
        return null;
      } catch {
        return null;
      }
    }
    return null;
  }

  private startRefreshTokenTimer(): void {
    this.stopRefreshTokenTimer();

    // Refresh 1 minute before expiry (tokens expire in 15 minutes = 900 seconds)
    const timeout = (900 - 60) * 1000; // 14 minutes in milliseconds

    this.refreshTokenTimeout = setTimeout(() => {
      this.refreshTokenTimeout = null;
      if (this.document.visibilityState === 'hidden') {
        this.refreshWhenVisible = true;
        return;
      }
      if (this.isUserIdle()) {
        this.refreshWhenActive = true;
        return;
      }
      this.refreshToken().subscribe({
        error: () => {
          // Invalid credentials expire the session; transport failures use a retry.
        }
      });
    }, timeout);
  }

  private stopRefreshTokenTimer(): void {
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
      this.refreshTokenTimeout = null;
    }
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let errorMessage = 'An unexpected error occurred';

    if (error.error?.error?.message) {
      errorMessage = error.error.error.message;
    } else if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return throwError(() => ({
      success: false,
      error: {
        code: error.status,
        message: errorMessage,
        details: error.error?.error?.details
      }
    }));
  };

  // Password validation helpers
  validatePassword(password: string): PasswordRequirements {
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};\\':"\\|,.<>\/?]/.test(password)
    };
  }

  isPasswordValid(password: string): boolean {
    const requirements = this.validatePassword(password);
    return Object.values(requirements).every(Boolean);
  }

  // Email validation
  isEmailValid(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Check if user is currently authenticated
  get isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  get currentUser(): User | null {
    return this.currentUserSubject.value;
  }

  private refreshWhenDocumentVisible(): void {
    if (this.document.visibilityState !== 'visible' || !this.refreshWhenVisible || !this.isAuthenticated) {
      return;
    }

    this.refreshWhenVisible = false;
    this.refreshToken().subscribe({
      error: () => {
        // A transient failure is retried without invalidating a still-valid session.
      }
    });
  }

  private recordUserActivity(): void {
    this.lastUserActivityAt = Date.now();
    if (!this.refreshWhenActive || !this.isAuthenticated) {
      return;
    }

    this.refreshWhenActive = false;
    this.refreshToken().subscribe({
      error: () => {
        // A transient failure is retried without invalidating a still-valid session.
      }
    });
  }

  private isUserIdle(): boolean {
    return Date.now() - this.lastUserActivityAt >= USER_IDLE_REFRESH_THRESHOLD_MS;
  }

  private scheduleRefreshRetry(): void {
    this.stopRefreshTokenTimer();
    if (!this.isAuthenticated) {
      return;
    }

    if (this.document.visibilityState === 'hidden') {
      this.refreshWhenVisible = true;
      return;
    }

    const retryDelayMs = this.refreshRetryDelayMs;
    this.refreshRetryDelayMs = Math.min(this.refreshRetryDelayMs * 2, REFRESH_RETRY_MAX_DELAY_MS);
    this.refreshTokenTimeout = setTimeout(() => {
      this.refreshTokenTimeout = null;
      if (this.isUserIdle()) {
        this.refreshWhenActive = true;
        return;
      }
      this.refreshToken().subscribe({
        error: () => {
          // Further transient failures continue through the bounded retry path.
        }
      });
    }, retryDelayMs);
  }

  setCurrentUser(user: User): void {
    this.storeUser(user);
    this.currentUserSubject.next(user);
  }

  handleSessionExpiry(returnUrl?: string): void {
    if (this.sessionExpiryInProgress) {
      return;
    }

    this.sessionExpiryInProgress = true;
    this.clearAuth();

    const nextReturnUrl = this.getSafeReturnUrl(returnUrl);
    const navigationExtras = nextReturnUrl
      ? { queryParams: { returnUrl: nextReturnUrl } }
      : undefined;

    void this.router.navigate(['/login'], navigationExtras).finally(() => {
      this.sessionExpiryInProgress = false;
    });
  }

  hasRole(...roles: PlatformRole[]): boolean {
    const currentUser = this.currentUser;
    if (!currentUser) {
      return false;
    }

    return roles.includes(currentUser.platformRole);
  }

  private getSafeReturnUrl(returnUrl?: string): string | null {
    const candidate = returnUrl || this.router.url || null;
    if (!candidate) {
      return null;
    }

    return this.isAuthRoute(candidate) ? null : candidate;
  }

  private isAuthRoute(url: string): boolean {
    return url.startsWith('/login')
      || url.startsWith('/register')
      || url.startsWith('/forgot-password')
      || url.startsWith('/reset-password');
  }
}

export interface MfaMethod {
  publicId: string;
  type: 'totp' | 'email';
  label: string;
}

export interface MfaChallenge {
  id: string;
  methods: MfaMethod[];
  expiresAt: string;
}

export interface AuthProviders {
  google: boolean;
}
